<template>
  <li
    ref="root"
    class="p-2 flex rounded-8 gap-4 cursor-pointer justify-between"
    :class="{
      'bg-charleston-green': isHovered || isSelected
    }"
    @mouseenter="onMouseEnter()"
    @mouseleave="onMouseLeave(() => menuOpened = false)"
  >
    <div class="flex items-center gap-4">
      <div class="relative">
        <contact-avatar
          :contact="member"
          :contact-type="member.type"
        />
        <contact-status
          v-if="member.imLogin"
          :p-served="member.imLogin"
          compact-mode
        />
      </div>
      <div class="flex flex-col justify-between py-1 min-w-0">
        <name-with-user-agent
          class="text-surf-lock-wht text-body-r-14-20"
          :name="member.name"
          :user-agent="participantUserAgents"
        />
        <div
          v-if="statusLabel"
          class="text-label-r-13-18 text-surf-lock-wht/70 mt-2"
        >
          {{ statusLabel }}
        </div>
      </div>
    </div>
    <div class="py-1 flex items-center">
      <div
        v-show="(!isHovered && !menuOpened) || isIAm || currentGuest || hideMenuButton"
        class="flex flex-col justify-between h-full gap-1"
      >
        <div v-if="!hideMenuButton">
          <div
            v-if="role && role !== 'User' && isModerated"
            class="text-1316 text-white text-right"
          >
            {{ rolesLabels[role] }}
          </div>
          <div
            v-else-if="isTemporary"
            class="text-1316 text-white text-right"
          >
            {{ $t('chat.temporary') }}
          </div>
        </div>
        <div class="text-white flex gap-2 items-center h-full justify-end min-w-[88px]">
          <wui-icon
            v-if="memberStatuses?.handEnabled"
            small
            name="handM"
          />
          <wui-icon
            v-if="(memberStatuses?.isAudioMuted && status === MemberStatus.Active) || isAudioMutedLocally"
            small
            name="micOffM"
          />
          <wui-icon
            v-if="memberStatuses?.isVideoMuted && status === MemberStatus.Active"
            small
            name="videoCameraOffM"
          />
          <wui-icon
            v-if="isVideoRecording"
            small
            name="radioButtonChecked"
          />
          <wui-icon
            v-if="isPinned"
            small
            name="pinnedM"
          />
          <slot name="status" />
        </div>
      </div>
      <div
        v-show="(isHovered || menuOpened) && !isIAm && !currentGuest && !hideMenuButton"
        class="w-[88px] h-10 flex items-center justify-end"
      >
        <wui-dropdown
          v-model="menuOpened"
          direction="left-bottom"
          close-on-click-inside
        >
          <template #activator="{ toggle }">
            <wui-btn
              class="bg-police-blue! text-black-200! relative shrink-0!"
              prepend-icon="kebabHorizontalM"
              icon
              :size="32"
              @click.stop="hideMenu ? emit('toggle-menu') : toggle()"
            />
          </template>
          <template #default>
            <call-session-member-menu
              :member="member"
              :room="room"
              :is-pinned="!!isPinned"
              @toggle-pin="emit('toggle-pin', member)"
              @see-profile-click="emit('see-profile-click', member)"
            />
          </template>
        </wui-dropdown>
      </div>
    </div>
  </li>
</template>

<script setup lang="ts">
import { WuiDropdown, WuiIcon, WuiBtn } from '@wui/common-library'
import { ConferenceMemberRole, ConferenceType } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'


import { CallSessionMemberMenu } from '@/features/call-session-member-menu'

import { useStoreConferenceMediaStatuses, ContactMediaStatuses } from '@/entities/conference-participant'
import { ContactAvatar } from '@/entities/contact-avatar'
import { MemberStatus } from '@/entities/room'

import { NameWithUserAgent } from '@/shared/components/user-agent-icon'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useStatusSubscribe } from '@/shared/composables/im'
import { useLocalization } from '@/shared/composables/localization'
import { useCoolDownHover } from '@/shared/composables/use-cool-down-hover'
import { ContactStatus } from '@/shared/contact-status'
import { useCallRecordLocalStore } from '@/shared/store/record-local'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'

const props = defineProps<{
  member: Contact
  role?: ConferenceMemberRole
  isTemporary?: boolean
  status: MemberStatus
  room: Room
  isSelected: boolean
  isPinned?: boolean
  callId: string
  hideMenu?: boolean
  hideMenuButton?: boolean
  participantIdForStatus?: string
}>()

const emit = defineEmits<{
  (event: 'toggle-pin', value: Contact): void
  (event: 'see-profile-click', value: Contact): void
  (event: 'toggle-menu'): void
}>()

const { t } = useLocalization()
const { currentUser } = useCurrentUser()
const {
  getIsLocallyMutedAudio,
  getStatusesByRoomAndPServed,
  getConferenceCallsByImLogin,
} = useStoreConferenceMediaStatuses()
const { recordingSubscribersByRoomPServed } = storeToRefs(useCallRecordLocalStore())
const { currentGuest } = useGuestAuth()
const { isHovered, onMouseEnter, onMouseLeave } = useCoolDownHover()

const root = ref()
const menuOpened = ref(false)

const isIAm = computed(() => currentUser.value?.imLogin === props.member.imLogin)

const participantUserAgents = computed(() => {
  const id = props.participantIdForStatus ?? props.member.imLogin
  if (!id) {
    return []
  }

  return [...new Set(
    getConferenceCallsByImLogin(props.room, id)
      .map(participant => participant.userAgent)
      .filter(Boolean),
  )]
})

const isModerated = computed(() => props.room.mode === ConferenceType.MODERATED)

const statusLabel = computed<string>(() => {
  if (props.status === MemberStatus.Active) {
    return t('calls.inConference')
  }

  if (props.status === MemberStatus.Dialing) {
    return t('calls.joins')
  }

  if (props.status === MemberStatus.OnHold) {
    return t('common.isOnHold')
  }

  if (props.status === MemberStatus.Awaiting) {
    return t('calls.waitingForConnection')
  }

  if (props.status === MemberStatus.RequestMedia) {
    return t('calls.requestConnection')
  }

  return ''
})

const rolesLabels = computed<{ [k in ConferenceMemberRole]: string }>(() => {
  return {
    Moderator: t('chat.moderator'),
    Owner: t('chat.owner'),
    User: t('chat.participant'),
    External: t('chat.external'),
  }
})

const itIsCurrentUser = computed(() => {
  return currentUser.value && props.member.imLogin && currentUser.value.imLogin === props.member.imLogin
})

const statusLookupId = computed(() => props.participantIdForStatus ?? props.member.imLogin)
const memberStatuses = computed<ContactMediaStatuses>(() => {
  const statuses = getStatusesByRoomAndPServed(props.room.pServedUser, statusLookupId.value)
  const firstStatus = statuses.at(0)
  if (statuses.length === 1 && firstStatus) {
    return firstStatus
  }

  let isAudioMuted = true
  let isVideoMuted = true
  statuses.forEach(s => {
    if (!s.isAudioMuted) {
      isAudioMuted = false
    }
    if (!s.isVideoMuted) {
      isVideoMuted = false
    }
  })

  return {
    id: statusLookupId.value,
    handEnabled: statuses[0] ? statuses[0].handEnabled : false,
    isAudioMuted,
    isVideoMuted,
    hasVideo: false,
    isScreenSharingMuted: false,
  }
})

const isVideoRecording = computed(() => recordingSubscribersByRoomPServed.value.get(props.room.pServedUser)?.has(props.member.imLogin))

const isAudioMutedLocally = computed(() => itIsCurrentUser.value ? getIsLocallyMutedAudio(props.callId) : false)

const { subscribe, unsubscribe } = useStatusSubscribe()

onMounted(() => {
  if (props.member.imLogin) {
    subscribe(props.member.imLogin)
  }
})

onUnmounted(() => {
  if (props.member.imLogin) {
    unsubscribe(props.member.imLogin)
  }
})

watch(() => props.member.imLogin, (newValue, oldValue) => {
  if (newValue) {
    subscribe(newValue)
  } else if (oldValue) {
    unsubscribe(oldValue)
  }
})
</script>
